/**
 * Memory Archive client — typed wrappers for the /memories, /entities, and
 * /timeline endpoints, plus the shared React Query key helpers.
 *
 * Every queryKey includes `targetUserId` so React Query treats an admin
 * "test as user" switch as a brand-new query (cache miss) instead of
 * serving the previous user's stale data. This is load-bearing — without
 * it, the picker switch would briefly show cross-user info while the new
 * fetch is in-flight.
 */
import type { LifePeriod, MemoryObject } from '@legacyorb/shared-types';

import { apiClient } from '@/services/api-client';

// ─── Shared key prefix ─────────────────────────────────────────────────────

const ROOT = ['memory-archive'] as const;

const tenant = (targetUserId?: string | null) => targetUserId ?? 'self';

// ─── Memories ──────────────────────────────────────────────────────────────

export interface MemoryListFilters {
  lifePeriod?: LifePeriod | null;
  themes?: string[];
  entity?: string | null;
  /** story_cards.id — filter to memories tagged to this card. */
  cardId?: string | null;
  limit?: number;
  offset?: number;
}

export interface MemoryListResult {
  items: MemoryObject[];
  total: number;
  limit: number;
}

export function memoriesQueryKey(targetUserId: string | null, filters: MemoryListFilters) {
  return [
    ...ROOT,
    'memories',
    tenant(targetUserId),
    {
      lifePeriod: filters.lifePeriod ?? null,
      themes: filters.themes ?? [],
      entity: filters.entity ?? null,
      cardId: filters.cardId ?? null,
      limit: filters.limit ?? 50,
      offset: filters.offset ?? 0,
    },
  ] as const;
}

export async function fetchMemories(
  targetUserId: string | null,
  filters: MemoryListFilters
): Promise<MemoryListResult> {
  const params: Record<string, string> = {};
  if (targetUserId) params.targetUserId = targetUserId;
  if (filters.lifePeriod) params.lifePeriod = filters.lifePeriod;
  if (filters.themes && filters.themes.length > 0) params.themes = filters.themes.join(',');
  if (filters.entity) params.entity = filters.entity;
  if (filters.cardId) params.cardId = filters.cardId;
  if (filters.limit) params.limit = String(filters.limit);
  if (filters.offset) params.offset = String(filters.offset);

  const response = await apiClient.get<MemoryObject[]>(
    '/api/v1/memories',
    Object.keys(params).length > 0 ? params : undefined
  );
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load memories');
  }
  return {
    items: (response.data as unknown as MemoryObject[]) ?? [],
    total: response.meta?.total ?? 0,
    limit: response.meta?.limit ?? filters.limit ?? 50,
  };
}

export function memoryDetailQueryKey(targetUserId: string | null, id: string | null) {
  return [...ROOT, 'memory', tenant(targetUserId), id] as const;
}

export async function fetchMemory(
  targetUserId: string | null,
  id: string
): Promise<MemoryObject | null> {
  const params = targetUserId ? { targetUserId } : undefined;
  const response = await apiClient.get<MemoryObject>(`/api/v1/memories/${id}`, params);
  if (!response.success) {
    if (response.error?.code === 'NOT_FOUND') return null;
    throw new Error(response.error?.message ?? 'Failed to load memory');
  }
  return (response.data as unknown as MemoryObject) ?? null;
}

export interface MemorySearchHit extends MemoryObject {
  similarity: number;
}

export function memorySearchQueryKey(targetUserId: string | null, q: string, topK: number) {
  return [...ROOT, 'search', tenant(targetUserId), q, topK] as const;
}

export async function searchMemories(
  targetUserId: string | null,
  q: string,
  topK: number = 10
): Promise<MemorySearchHit[]> {
  const params: Record<string, string> = { q, topK: String(topK) };
  if (targetUserId) params.targetUserId = targetUserId;
  const response = await apiClient.get<MemorySearchHit[]>('/api/v1/memories/search', params);
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to search memories');
  }
  return (response.data as unknown as MemorySearchHit[]) ?? [];
}

// ─── Memory card counts (Story Cards filter) ─────────────────────────────────

export interface MemoryCardCount {
  /** story_cards.id (UUID) — what we filter the memory list by. */
  cardUuid: string;
  /** Human-readable card id, e.g. "C001". */
  cardId: string;
  title: string;
  depth: number;
  groupName: string | null;
  /** Distinct memories tagged to this card (relevance >= floor). */
  count: number;
}

export function memoryCardCountsQueryKey(targetUserId: string | null) {
  return [...ROOT, 'card-counts', tenant(targetUserId)] as const;
}

export async function fetchMemoryCardCounts(
  targetUserId: string | null
): Promise<MemoryCardCount[]> {
  const params = targetUserId ? { targetUserId } : undefined;
  const response = await apiClient.get<MemoryCardCount[]>('/api/v1/memories/card-counts', params);
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load story-card counts');
  }
  return (response.data as unknown as MemoryCardCount[]) ?? [];
}

// ─── Entities ──────────────────────────────────────────────────────────────

export interface EntityRollup {
  name: string;
  relationship?: string;
  mentionCount: number;
  firstMentioned: string;
  lastMentioned: string;
  memoryIds: string[];
  avgEmotionalDensity: number;
}

export function peopleQueryKey(targetUserId: string | null) {
  return [...ROOT, 'people', tenant(targetUserId)] as const;
}

export async function fetchPeople(targetUserId: string | null): Promise<EntityRollup[]> {
  const params = targetUserId ? { targetUserId } : undefined;
  const response = await apiClient.get<EntityRollup[]>('/api/v1/entities/people', params);
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load people');
  }
  return (response.data as unknown as EntityRollup[]) ?? [];
}

export function placesQueryKey(targetUserId: string | null) {
  return [...ROOT, 'places', tenant(targetUserId)] as const;
}

export async function fetchPlaces(targetUserId: string | null): Promise<EntityRollup[]> {
  const params = targetUserId ? { targetUserId } : undefined;
  const response = await apiClient.get<EntityRollup[]>('/api/v1/entities/places', params);
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load places');
  }
  return (response.data as unknown as EntityRollup[]) ?? [];
}

// ─── Timeline ──────────────────────────────────────────────────────────────

export interface TimelineEntry {
  memoryId: string;
  name: string;
  date?: string;
  endDate?: string;
  confidence: 'high' | 'medium' | 'low';
  significance?: string;
  emotionalDensity: number;
  source: 'event' | 'date-range';
}

export function timelineQueryKey(targetUserId: string | null) {
  return [...ROOT, 'timeline', tenant(targetUserId)] as const;
}

export async function fetchTimeline(targetUserId: string | null): Promise<TimelineEntry[]> {
  const params = targetUserId ? { targetUserId } : undefined;
  const response = await apiClient.get<TimelineEntry[]>('/api/v1/timeline', params);
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load timeline');
  }
  return (response.data as unknown as TimelineEntry[]) ?? [];
}
